import React, { useState } from 'react'
import { PageProps } from '@/types'
import { Answer as AnswerType } from '@/types/admin'
import AnswersTab from '@/components/Admin/Answers/AnswersTab'
import SummaryTab from '@/components/Admin/Answers/SummaryTab'
import Filters from '@/components/Admin/Answers/Filters'

interface QuestionSummary {
  question_body: string
  kind: string
  answers?: Record<string, number>
  word_cloud_data?: Array<{ text: string; value: number }>
  total_responses: number
}

interface AnswersIndexProps extends PageProps {
  answers: AnswerType[]
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
  }
  survey: {
    id: number
    name: string
    service_name?: string
  }
  filters: {
    device_types: string[]
    browsers: string[]
    csat_options: string[]
    nps_options: string[]
    contact_options: string[]
    disability_types: string[]
  }
  summary_data: Record<string, QuestionSummary>
  metrics: {
    csat_score: number
    nps_score: number
    accessibility_csat_score: number
  }
}

const Index: React.FC<AnswersIndexProps> = ({ answers, pagination, filters, survey, summary_data, metrics }) => {
  const [activeTab, setActiveTab] = useState<'answers' | 'summary'>('answers')

  return (
    <div className="mx-auto py-10">
      {/* Header */}
      <div className="mb-10 container">
        <h1 className="text-2xl font-bold mb-2">Respuestas de {survey.service_name || survey.name}</h1>
        <div className="mb-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            Total de respuestas: {pagination.total_count}
          </span>
        </div>
        <p className="text-gray-600">
          Conoce los indicadores principales utilizados: la Puntuación de Satisfacción del Cliente (CSAT) y la
          Puntuación Neta de Promotores (NPS), por sus siglas en inglés respectivamente.
        </p>
        <a href="#" className="text-blue-600 hover:text-blue-800 text-sm">
          Infórmate más →
        </a>
      </div>

      <div className="bg-blue-50">
        <div className="container mx-auto pt-10">
          <div className="mb-8">
            <Filters dataFilters={filters} />
          </div>
          {/* Tabs */}
          <div className="">
            <div className=" rounded-lg inline-flex items-center">
              <button
                onClick={() => setActiveTab('answers')}
                className={`px-10 py-6 text-sm font-medium rounded-t-md transition-colors bg-blue-200 ${
                  activeTab === 'answers' ? 'bg-white text-gray-800' : 'text-gray-800 hover:bg-white/60'
                }`}
              >
                <span className="flex items-center gap-2">
                  Respuestas
                  <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full ${
                    activeTab === 'answers' ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white'
                  }`}>
                    {pagination.total_count}
                  </span>
                </span>
              </button>
              <div className="bg-blue-50 px-4 self-stretch flex items-center">{/* This is the styled separator */}</div>
              <button
                onClick={() => setActiveTab('summary')}
                className={`px-10 py-6 text-sm font-medium rounded-t-md transition-colors bg-blue-200 ${
                  activeTab === 'summary' ? 'bg-white text-gray-800' : 'text-gray-800 hover:bg-white/60'
                }`}
              >
                <span className="flex items-center gap-2">
                  Resumen
                  <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full ${
                    activeTab === 'summary' ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white'
                  }`}>
                    {pagination.total_count}
                  </span>
                </span>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'answers' ? (
            <AnswersTab answers={answers} pagination={pagination} />
          ) : (
            <SummaryTab answers={answers} survey={survey} summaryData={summary_data} metrics={metrics} />
          )}
        </div>
      </div>
    </div>
  )
}

export default Index
